import {
  Briefcase,
  Headphones,
  Target,
  BarChart3,
  UserCheck,
  ShieldCheck,
  BadgeCheck,
  TrendingUp,
  Handshake,
  Scale,
  HardHat,
  Cpu,
  ShoppingCart,
  Landmark,
  HeartPulse,
  Rocket,
  FileSearch,
  Kanban,
  FolderKanban,
  LineChart,
  Activity,
  Calculator,
  ClipboardCheck,
  PieChart,
  Megaphone,
  Server,
} from "lucide-react";

export const MARQUEE_ITEMS = [
  "Business Analysts",
  "Project Managers",
  "Customer Operations",
  "Data Analysts",
  "Lead Generation",
  "Finance & Accounting",
  "Executive Assistance",
  "QA Analysts",
  "Operations Support",
  "Reporting Specialists",
  "PMO Analysts",
  "Social Media & Marketing",
  "IT Services",
  "CRM Administration",
];

export const PROBLEMS = [
  {
    num: "01",
    title: "Hiring takes time",
    copy: "Find qualified people without spending months recruiting.",
  },
  {
    num: "02",
    title: "Overhead adds up",
    copy: "Expand capacity without adding every employment cost to your internal structure.",
  },
  {
    num: "03",
    title: "Managing remote talent is difficult",
    copy: "We provide structure, oversight and accountability around the team.",
  },
];

export const SERVICES = [
  {
    icon: Briefcase,
    name: "Business Operations",
    copy: "Dedicated professionals supporting the day-to-day running of your business.",
    examples: [
      "Administrative support",
      "Executive assistance",
      "Data entry",
      "CRM administration",
      "Document management",
      "Scheduling",
      "Reporting",
      "Back-office operations",
    ],
    cta: "Explore Business Operations",
    href: "/solutions#business-operations",
  },
  {
    icon: Headphones,
    name: "Customer Operations",
    copy: "Build a dedicated customer support function without building the entire infrastructure yourself.",
    examples: [
      "Customer service",
      "Email support",
      "Chat support",
      "Inbound calls",
      "Order management",
      "Customer administration",
      "CRM support",
    ],
    cta: "Explore Customer Operations",
    href: "/solutions#customer-operations",
  },
  {
    icon: Target,
    name: "Sales & Lead Generation",
    copy: "Give your sales team more capacity.",
    examples: [
      "Lead generation",
      "Prospect research",
      "Outbound calling",
      "Appointment setting",
      "CRM management",
      "Sales administration",
      "Pipeline support",
    ],
    cta: "Explore Sales Solutions",
    href: "/solutions#sales-lead-generation",
  },
  {
    icon: Megaphone,
    name: "Social Media & Marketing",
    copy: "Build a consistent, professional presence without doing it all yourself.",
    examples: [
      "Social media management",
      "Content scheduling",
      "Community management",
      "Copywriting support",
      "Graphic design support",
      "Email marketing support",
      "Marketing administration",
    ],
    cta: "Explore Social Media & Marketing",
    href: "/solutions#social-media-marketing",
  },
  {
    icon: BarChart3,
    name: "Professional Services",
    copy: "Access experienced professionals for specialist business functions.",
    examples: [
      "Business Analysts",
      "Project Managers",
      "PMO Analysts",
      "Data Analysts",
      "Finance & Accounting professionals",
      "Operations Analysts",
      "QA Analysts",
      "IT specialists",
      "Reporting specialists",
    ],
    cta: "Explore Professional Services",
    href: "/professional-services",
    featured: true,
  },
];

export const BENEFITS = [
  {
    icon: UserCheck,
    title: "Dedicated professionals",
    copy: "Your team members are dedicated to your business rather than treated as interchangeable resources.",
  },
  {
    icon: ShieldCheck,
    title: "Managed delivery",
    copy: "We provide structure, oversight and accountability around your outsourced team.",
  },
  {
    icon: BadgeCheck,
    title: "Professional capability",
    copy: "Go beyond administrative support with experienced professionals across specialist business functions.",
  },
  {
    icon: TrendingUp,
    title: "Flexible scaling",
    copy: "Start with one role or build a larger team as your business grows.",
  },
  {
    icon: Handshake,
    title: "One accountable partner",
    copy: "Recruitment, onboarding, coordination and ongoing management are handled through one partner.",
  },
];

export const STEPS = [
  {
    num: "01",
    title: "Tell us what you need",
    copy: "Share the role, responsibilities, skills and outcomes you're looking for.",
  },
  {
    num: "02",
    title: "We find the right talent",
    copy: "We source and screen candidates against your requirements.",
  },
  {
    num: "03",
    title: "Meet your candidates",
    copy: "Review shortlisted professionals and choose the people who fit your business.",
  },
  {
    num: "04",
    title: "Build and scale",
    copy: "Your team gets onboarded, supported and managed as an extension of your business.",
  },
];

export const INDUSTRIES = [
  {
    icon: Scale,
    name: "Professional Services",
    copy: "Analysts, coordinators and support professionals who extend your firm's delivery capacity.",
    roles: ["Business Analysts", "Project Support", "Reporting"],
  },
  {
    icon: HardHat,
    name: "Construction & Home Services",
    copy: "Estimating support, scheduling, customer coordination and back-office capacity for trades and builders.",
    roles: ["Scheduling", "Estimating Support", "Customer Admin"],
  },
  {
    icon: Cpu,
    name: "SaaS & Technology",
    copy: "Customer operations, QA, data and sales support that scales with your product growth.",
    roles: ["QA Analysts", "Customer Support", "Data Analysts"],
  },
  {
    icon: ShoppingCart,
    name: "E-commerce",
    copy: "Order management, customer service and operational support across your sales channels.",
    roles: ["Order Management", "Chat Support", "Catalog Admin"],
  },
  {
    icon: Landmark,
    name: "Financial Services",
    copy: "Finance, accounting and reporting professionals working within your compliance framework.",
    roles: ["Bookkeeping", "Reporting", "Analysis"],
  },
  {
    icon: HeartPulse,
    name: "Healthcare Administration",
    copy: "Administrative and coordination support for practices and healthcare businesses.",
    roles: ["Scheduling", "Billing Support", "Patient Admin"],
  },
  {
    icon: Rocket,
    name: "Growing SMBs",
    copy: "Flexible capacity across operations, sales and finance for businesses on the way up.",
    roles: ["Admin Support", "Lead Generation", "Executive Assistance"],
  },
];

export const PRICING = [
  {
    name: "Business Operations",
    rate: "$8",
    unit: "/hour",
    note: "For admin, data and back-office support roles",
    features: ["Dedicated team member", "Managed onboarding", "Ongoing oversight", "Flexible hours"],
  },
  {
    name: "Customer Operations",
    rate: "$8",
    unit: "/hour",
    note: "For support, chat, email and inbound roles",
    features: ["Dedicated support professional", "Coverage aligned to your hours", "Quality monitoring", "Flexible scaling"],
  },
  {
    name: "Sales & Lead Generation",
    rate: "$8",
    unit: "/hour",
    note: "For prospecting, outbound and pipeline support",
    features: ["Trained on your offer", "CRM-managed activity", "Appointment setting", "Pipeline reporting"],
  },
  {
    name: "Professional Services",
    rate: "Custom",
    unit: "",
    note: "Priced per role, seniority and scope of engagement",
    features: ["Experienced specialists", "Structured deliverables", "Direct collaboration", "Managed engagement"],
  },
];

export const FAQS = [
  {
    q: "Where are your teams based?",
    a: "Our delivery professionals are based in South Asia, supported by Australian-led management and oversight. Your dedicated team members work from our South Asia delivery locations, with structured processes around quality, communication and delivery.",
  },
  {
    q: "Are team members dedicated to my business?",
    a: "Yes. Dedicated team arrangements are available depending on the role and engagement. Your team members work on your business, in your systems, aligned to your processes, not shared across unrelated clients.",
  },
  {
    q: "Can I start with one person?",
    a: "Yes. Many customers start with a single role and scale their team as requirements grow. There is no pressure to build a large team on day one.",
  },
  {
    q: "What types of roles can you provide?",
    a: "Business support, customer operations, sales and lead generation, finance and accounting, business analysts, project managers, data analysts, QA analysts, IT support, reporting specialists and other professional roles.",
  },
  {
    q: "How does onboarding work?",
    a: "We start with a consultation to define the role, responsibilities and outcomes. We then source and screen candidates, you meet and select the people who fit, and we manage onboarding so your new team members are productive quickly.",
  },
  {
    q: "Do you manage the team?",
    a: "Yes. Synergy Outsourcing operates a managed delivery model. We provide structure, oversight, coordination and accountability around your team, so you get the output of a well-run team without building the management layer yourself.",
  },
  {
    q: "Can you work with Australian business hours?",
    a: "Yes. Delivery arrangements are structured around Australian business hours or the hours that suit you best, including overlap with your time zone for meetings, handoffs and real-time collaboration.",
  },
];

export const CASE_STUDIES = [
  {
    tag: "Operations Support",
    location: "Melbourne, AU",
    title: "Clearing an admin backlog and winning back capacity",
    challenge: "A growing Melbourne services business had senior staff buried in administration, scheduling and document handling, and valuable work was starting to slip.",
    approach: "Synergy Outsourcing defined the support roles, sourced dedicated professionals and established a managed team working inside the client's existing systems.",
    model: "Dedicated business operations team with Australian-led oversight and structured weekly reporting.",
    roles: ["Admin Support", "Scheduling", "Reporting"],
    stats: [
      { value: "3", label: "Dedicated team members" },
      { value: "18 hrs/wk", label: "Freed for senior staff" },
    ],
  },
  {
    tag: "Lead Generation",
    location: "Sydney, AU",
    title: "Building a repeatable outbound pipeline engine",
    challenge: "A Sydney sales-led organisation had strong closers but an inconsistent pipeline, with senior staff spending hours on prospect research.",
    approach: "A managed outbound team took over prospect research, list building and appointment setting, working to agreed activity and quality standards.",
    model: "Managed sales development team aligned to client hours with CRM-tracked activity.",
    roles: ["Prospect Research", "Outbound Calling", "Appointment Setting"],
    stats: [
      { value: "284", label: "Qualified meetings in 6 months" },
      { value: "4.1x", label: "Increase in pipeline value" },
    ],
  },
  {
    tag: "Professional Services",
    location: "Brisbane, AU",
    title: "Adding analytical firepower without senior local hires",
    challenge: "A scaling Brisbane organisation needed analytical and project capability but could not justify multiple senior local hires at once.",
    approach: "Synergy Outsourcing embedded a dedicated analyst and project support professional as an extension of the internal team, with structured deliverables.",
    model: "Dedicated professional services roles with direct collaboration and managed engagement oversight.",
    roles: ["Business Analyst", "Data Analysis", "Project Support"],
    stats: [
      { value: "2", label: "Embedded specialists" },
      { value: "9 days", label: "Average time to interview-ready" },
    ],
  },
];

export const ROLES = [
  {
    icon: FileSearch,
    name: "Business Analysts",
    tagline: "Structured thinking for better business decisions.",
    responsibilities: [
      "Gather and document business requirements",
      "Map and improve processes and workflows",
      "Analyse operations and identify inefficiencies",
      "Support system and tooling implementations",
    ],
    skills: ["Requirements gathering", "Process mapping", "Stakeholder communication", "Documentation", "Excel / BI tools"],
    deliverables: ["Requirement documents", "Process maps", "Gap analyses", "Improvement recommendations"],
    suited: "Professional services firms, SaaS companies and growing SMBs systemising how they operate.",
    support: "Synergy Outsourcing sources and screens analysts against your requirements, onboards them into your tools and stays accountable for delivery through structured check-ins.",
  },
  {
    icon: Kanban,
    name: "Project Managers",
    tagline: "Keep initiatives moving without adding headcount.",
    responsibilities: [
      "Plan and coordinate projects end to end",
      "Manage timelines, dependencies and risks",
      "Run stand-ups, status updates and reporting",
      "Coordinate internal and external stakeholders",
    ],
    skills: ["Project planning", "Risk management", "Communication", "Asana / Jira / Monday", "Agile & waterfall methods"],
    deliverables: ["Project plans", "Status reports", "Risk registers", "Milestone tracking"],
    suited: "Businesses running multiple concurrent initiatives that need consistent delivery discipline.",
    support: "We match PMs to your methodology and tooling, establish reporting rhythms and provide oversight so nothing slips silently.",
  },
  {
    icon: FolderKanban,
    name: "PMO Analysts",
    tagline: "Portfolio-level visibility and governance support.",
    responsibilities: [
      "Maintain project portfolios and dashboards",
      "Standardise templates and reporting",
      "Track budgets, resources and milestones",
      "Support governance and steering processes",
    ],
    skills: ["Portfolio reporting", "Data consolidation", "Governance frameworks", "Advanced Excel", "PMO tooling"],
    deliverables: ["Portfolio dashboards", "Governance packs", "Resource reports", "Standardised templates"],
    suited: "Organisations with growing project volumes that need structure without a full PMO build-out.",
    support: "Synergy Outsourcing provides PMO analysts experienced in established frameworks, managed within your governance cadence.",
  },
  {
    icon: LineChart,
    name: "Data Analysts",
    tagline: "Turn scattered data into decisions.",
    responsibilities: [
      "Clean, structure and analyse business data",
      "Build and maintain dashboards",
      "Produce recurring and ad-hoc analysis",
      "Identify trends, anomalies and opportunities",
    ],
    skills: ["SQL", "Excel / Google Sheets", "Power BI / Tableau", "Data cleaning", "Visualisation"],
    deliverables: ["Dashboards", "Analysis reports", "Data models", "Insight summaries"],
    suited: "Businesses sitting on data they don't have time to analyse properly.",
    support: "We vet analytical capability upfront, align on your data stack and manage delivery against agreed outputs.",
  },
  {
    icon: Activity,
    name: "Operations Analysts",
    tagline: "Visibility into how the business actually runs.",
    responsibilities: [
      "Monitor operational metrics and KPIs",
      "Analyse workflows and capacity",
      "Support process improvement initiatives",
      "Prepare operational reporting",
    ],
    skills: ["KPI reporting", "Process analysis", "Excel / BI tools", "Problem solving", "Documentation"],
    deliverables: ["KPI reports", "Capacity analyses", "Process documentation", "Improvement proposals"],
    suited: "Operations-heavy businesses that need tighter control as they scale.",
    support: "Your analyst works inside your operating rhythm, with Synergy Outsourcing providing structure and accountability around outputs.",
  },
  {
    icon: Calculator,
    name: "Finance & Accounting",
    tagline: "Accurate books and sharper financial visibility.",
    responsibilities: [
      "Bookkeeping and reconciliations",
      "Accounts payable and receivable",
      "Management reporting support",
      "Payroll and compliance administration",
    ],
    skills: ["Bookkeeping", "Xero / QuickBooks", "Reconciliations", "AP / AR", "Financial reporting"],
    deliverables: ["Reconciled accounts", "AP / AR processing", "Month-end support", "Management reports"],
    suited: "Businesses needing reliable finance capacity without the cost of a full local hire.",
    support: "We match finance professionals to your systems and standards, with review processes that keep accuracy high.",
  },
  {
    icon: ClipboardCheck,
    name: "QA Analysts",
    tagline: "Consistent quality across product and process.",
    responsibilities: [
      "Execute manual testing and test plans",
      "Document and track defects",
      "Validate releases and changes",
      "Support process quality checks",
    ],
    skills: ["Manual testing", "Test case design", "Defect tracking", "Attention to detail", "Jira / TestRail"],
    deliverables: ["Test plans", "Defect reports", "Release sign-off support", "Regression coverage"],
    suited: "SaaS and technology teams that need dependable QA capacity as release cycles accelerate.",
    support: "Synergy Outsourcing embeds QA analysts into your development workflow with managed oversight of quality standards.",
  },
  {
    icon: Server,
    name: "IT Services",
    tagline: "Dependable technical capability without the in-house overhead.",
    responsibilities: [
      "Provide helpdesk and technical support",
      "Maintain systems, user access and devices",
      "Support software rollouts and integrations",
      "Document IT processes and procedures",
    ],
    skills: ["Helpdesk support", "Microsoft 365 / Google Workspace", "Systems administration", "Troubleshooting", "Documentation"],
    deliverables: ["Resolved support tickets", "System and access documentation", "Onboarding and offboarding setups", "IT process guides"],
    suited: "Businesses that need reliable IT support and systems capability without hiring a full internal IT team.",
    support: "Synergy Outsourcing matches IT professionals to your environment and manages delivery against agreed service levels.",
  },
  {
    icon: PieChart,
    name: "Reporting Specialists",
    tagline: "Reliable reporting, delivered on schedule.",
    responsibilities: [
      "Build and maintain recurring reports",
      "Consolidate data from multiple systems",
      "Automate manual reporting work",
      "Prepare leadership and board packs",
    ],
    skills: ["Advanced Excel", "BI platforms", "Data consolidation", "Report automation", "Presentation"],
    deliverables: ["Scheduled reports", "Leadership dashboards", "Automated reporting flows", "Board-ready packs"],
    suited: "Leadership teams spending too many hours assembling reports manually.",
    support: "We establish your reporting calendar, build the capability around it and manage delivery to deadlines.",
  },
];

export const SOLUTIONS = [
  {
    id: "business-operations",
    icon: Briefcase,
    name: "Business Operations",
    headline: "The day-to-day, handled.",
    intro: "Dedicated professionals supporting the day-to-day running of your business, so your senior people can focus on growth.",
    provide: "We provide dedicated business operations professionals who work inside your systems, follow your processes and become a genuine extension of your team.",
    roles: ["Administrative support", "Executive assistance", "Data entry", "CRM administration", "Document management", "Scheduling", "Reporting", "Back-office operations"],
    responsibilities: ["Managing inboxes, calendars and coordination", "Maintaining accurate records and data", "Preparing documents, reports and presentations", "Keeping your CRM and systems up to date"],
    suited: ["Founders spending too much time on admin", "Teams with growing back-office workload", "Businesses systemising operations for scale"],
    engagement: ["Define the role and outcomes together", "We source and screen candidates", "You interview and select your team member", "We manage onboarding and ongoing delivery"],
  },
  {
    id: "customer-operations",
    icon: Headphones,
    name: "Customer Operations",
    headline: "A support function without the infrastructure.",
    intro: "Build a dedicated customer support function without building the entire infrastructure yourself.",
    provide: "We build customer operations teams trained on your business, your tone and your standards, with quality monitoring and management built in.",
    roles: ["Customer service", "Email support", "Chat support", "Inbound calls", "Order management", "Customer administration", "CRM support"],
    responsibilities: ["Responding to customers across channels", "Managing orders, bookings and requests", "Maintaining customer records", "Escalating issues with clear handoffs"],
    suited: ["Businesses missing calls or slow to respond", "E-commerce and service businesses scaling support", "Companies needing coverage beyond local hours"],
    engagement: ["Map your support workflows and standards", "We recruit and train your dedicated team", "Coverage aligned to your operating hours", "Ongoing quality oversight and reporting"],
  },
  {
    id: "sales-lead-generation",
    icon: Target,
    name: "Sales & Lead Generation",
    headline: "More pipeline, without more payroll.",
    intro: "Give your sales team more capacity with dedicated people focused on filling and managing your pipeline.",
    provide: "We provide managed sales support professionals who handle the top and middle of your funnel, so your closers spend their time closing.",
    roles: ["Lead generation", "Prospect research", "Outbound calling", "Appointment setting", "CRM management", "Sales administration", "Pipeline support"],
    responsibilities: ["Building targeted prospect lists", "Running structured outbound activity", "Booking qualified appointments", "Keeping pipeline data clean and current"],
    suited: ["Sales-led businesses with inconsistent pipeline", "Founders still doing their own prospecting", "Teams launching into new segments or markets"],
    engagement: ["Define your ideal customer and offer", "We build and train your outbound team", "Activity tracked in your CRM", "Weekly performance and pipeline reporting"],
  },
  {
    id: "social-media-marketing",
    icon: Megaphone,
    name: "Social Media & Marketing",
    headline: "A consistent presence, without the daily grind.",
    intro: "Dedicated social media and marketing professionals who keep your brand visible, active and on-message.",
    provide: "We provide dedicated marketing support professionals who manage your channels, content calendar and audience engagement, working to your brand voice and business goals.",
    roles: ["Social media management", "Content scheduling", "Community management", "Graphic design support", "Copywriting support", "Email marketing support", "Marketing administration", "Campaign coordination"],
    responsibilities: ["Planning and publishing content across your channels", "Engaging with your audience and managing comments and messages", "Maintaining a consistent content calendar", "Reporting on reach, engagement and growth"],
    suited: ["Businesses too busy to post consistently", "Brands wanting a professional presence without an agency retainer", "Teams needing reliable marketing execution support"],
    engagement: ["Define your brand voice, channels and goals together", "We source marketing professionals with relevant experience", "Content planned and approved on your schedule", "Regular reporting on activity and results"],
  },
  {
    id: "finance-accounting",
    icon: Calculator,
    name: "Finance & Accounting",
    headline: "Reliable finance capacity, on tap.",
    intro: "Access finance and accounting professionals who keep your numbers accurate, current and useful.",
    provide: "We provide dedicated finance professionals, from bookkeeping through to reporting support, matched to your systems and standards.",
    roles: ["Bookkeepers", "AP / AR specialists", "Payroll administrators", "Financial reporting analysts", "Accounting support"],
    responsibilities: ["Processing and reconciling transactions", "Managing payables and receivables", "Supporting month-end close", "Preparing management reporting"],
    suited: ["Businesses outgrowing DIY bookkeeping", "Finance teams needing extra capacity at month-end", "Companies wanting better reporting without a senior hire"],
    engagement: ["Assess your finance workflows and systems", "We match qualified finance professionals", "Work to your review and approval processes", "Managed quality and deadline oversight"],
  },
  {
    id: "it-services",
    icon: Server,
    name: "IT Services",
    headline: "Dependable IT capability, without the in-house cost.",
    intro: "Dedicated IT professionals who keep your systems running and your people supported.",
    provide: "We provide dedicated IT support professionals who manage day-to-day technical needs, user support and systems administration within your environment.",
    roles: ["Helpdesk support", "IT support specialists", "Systems administration", "Network support", "Software support", "IT documentation"],
    responsibilities: ["Resolving staff technical issues and tickets", "Managing user accounts, access and devices", "Maintaining systems and standard software", "Documenting IT processes and procedures"],
    suited: ["Growing businesses without internal IT", "Teams needing reliable helpdesk coverage", "Companies wanting IT support without a full-time hire"],
    engagement: ["Map your systems, tools and support needs", "We source IT professionals with matching experience", "Your dedicated support works in your environment", "Managed service levels and regular reporting"],
  },
  {
    id: "professional-services",
    icon: BarChart3,
    name: "Professional Services",
    headline: "Specialist capability, embedded in your team.",
    intro: "Don't just outsource tasks. Add professional capability: analysts, project managers and specialists who operate as part of your business.",
    provide: "We provide experienced professionals across analytical, project and specialist functions, working as an extension of your internal team.",
    roles: ["Business Analysts", "Project Managers", "PMO Analysts", "Data Analysts", "Operations Analysts", "QA Analysts", "IT specialists", "Reporting specialists"],
    responsibilities: ["Delivering defined specialist outputs", "Collaborating directly with your team", "Working to your standards and methodologies", "Reporting progress with full transparency"],
    suited: ["Firms needing specialist skills without full-time hires", "Businesses scaling delivery capability", "Leaders who need senior output on a flexible basis"],
    engagement: ["Scope the role, skills and deliverables", "Meet shortlisted, pre-screened professionals", "Embedded onboarding into your team", "Structured oversight for the life of the engagement"],
  },
];

export const PRINCIPLES = [
  {
    title: "Professionalism first",
    copy: "Every engagement is built around capable professionals, clear standards and respectful, business-grade communication.",
  },
  {
    title: "Accountability, built in",
    copy: "Structure, oversight and a single accountable partner, so managed actually means managed.",
  },
  {
    title: "Long-term relationships",
    copy: "We build teams intended to last, not churn. The goal is a durable extension of your business.",
  },
  {
    title: "Structured delivery",
    copy: "Defined roles, documented processes and consistent reporting from day one.",
  },
  {
    title: "Global talent, responsibly",
    copy: "We access global professionals and invest in their success, because great teams are built, not extracted.",
  },
  {
    title: "Outcomes over activity",
    copy: "We align every engagement to business outcomes, not billable busywork.",
  },
];
